import { useState } from "react";
import type { Bootstrap, PaymentPlan } from "../../shared/types";
import { api, type PlanInput } from "../lib/api";
import { parseMoney, currentPeriod } from "../lib/format";
import Modal from "./Modal";
import Select from "./Select";
import { useConfirm } from "./ConfirmProvider";

// Add or edit a payment plan (Klarna, Clearpay, PayPal Pay in 3, …).
export default function PlanModal({
  data,
  plan,
  onClose,
  onSaved,
}: {
  data: Bootstrap;
  plan: PaymentPlan | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const editing = plan != null;
  const [name, setName] = useState(plan?.name ?? "");
  const [provider, setProvider] = useState(plan?.provider ?? "");
  const [amount, setAmount] = useState(plan ? (plan.amountMinor / 100).toFixed(2) : "");
  const [firstAmount, setFirstAmount] = useState(plan?.firstAmountMinor != null ? (plan.firstAmountMinor / 100).toFixed(2) : "");
  const [lastAmount, setLastAmount] = useState(plan?.lastAmountMinor != null ? (plan.lastAmountMinor / 100).toFixed(2) : "");
  const [numPayments, setNumPayments] = useState(plan ? String(plan.numPayments) : "");
  const [startPeriod, setStartPeriod] = useState(plan?.startPeriod ?? currentPeriod());
  const [dueDay, setDueDay] = useState(plan?.dueDay != null ? String(plan.dueDay) : "1");
  const [categoryId, setCategoryId] = useState(
    plan ? String(data.categories.find((c) => c.name === plan.categoryName)?.id ?? "") : "",
  );
  const [paidBy, setPaidBy] = useState(plan?.paidById ? String(plan.paidById) : "");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function save() {
    const minor = parseMoney(amount);
    const n = Number(numPayments);
    if (!name.trim()) return setError("Give the plan a name");
    if (minor == null || minor <= 0) return setError("Enter a valid payment amount");
    if (!Number.isInteger(n) || n < 1) return setError("Number of payments must be 1 or more");
    if (!/^\d{4}-\d{2}$/.test(startPeriod)) return setError("Pick a start month");
    let firstMinor: number | null = null;
    if (firstAmount.trim() !== "") {
      firstMinor = parseMoney(firstAmount);
      if (firstMinor == null || firstMinor <= 0) return setError("Deposit / first payment must be a valid amount");
    }
    let lastMinor: number | null = null;
    if (lastAmount.trim() !== "") {
      lastMinor = parseMoney(lastAmount);
      if (lastMinor == null || lastMinor <= 0) return setError("Last payment must be a valid amount");
    }
    const payload: PlanInput = {
      name: name.trim(),
      provider: provider.trim() || null,
      categoryId: categoryId ? Number(categoryId) : null,
      paidBy: paidBy ? Number(paidBy) : null,
      amountMinor: minor,
      firstAmountMinor: firstMinor,
      lastAmountMinor: lastMinor,
      numPayments: n,
      frequency: "monthly",
      startPeriod,
      dueDay: dueDay ? Number(dueDay) : null,
    };
    setSaving(true);
    try {
      if (editing) await api.updatePlan(plan.id, payload);
      else await api.createPlan(payload);
      onSaved();
    } catch (e) {
      setError((e as Error).message);
      setSaving(false);
    }
  }

  const ask = useConfirm();

  async function remove() {
    if (!plan) return;
    if (!(await ask({ title: "Archive plan?", message: `Archive plan "${plan.name}"? It moves to your archived plans and stops counting, but stays in your history. (All payments must be marked complete first.)`, confirmLabel: "Archive" }))) return;
    setSaving(true);
    try {
      await api.deletePlan(plan.id);
      onSaved();
    } catch (e) {
      setError((e as Error).message);
      setSaving(false);
    }
  }

  return (
    <Modal title={editing ? "Edit plan" : "Add plan"} onClose={onClose}>
      <div className="form">
        <label className="field">
          <span className="field__label">What is it?</span>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="New golf clubs" autoFocus />
        </label>
        <div className="form__row">
          <label className="field">
            <span className="field__label">Provider</span>
            <input className="input" value={provider} onChange={(e) => setProvider(e.target.value)} placeholder="Klarna" />
          </label>
          <label className="field">
            <span className="field__label">Payment amount (£)</span>
            <input className="input" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="83.92" />
          </label>
        </div>
        <div className="form__row">
          <label className="field">
            <span className="field__label">Number of payments</span>
            <input className="input" inputMode="numeric" value={numPayments} onChange={(e) => setNumPayments(e.target.value)} placeholder="6" />
          </label>
          <label className="field">
            <span className="field__label">First payment month</span>
            <input className="input" type="month" value={startPeriod} onChange={(e) => setStartPeriod(e.target.value)} />
          </label>
        </div>
        <div className="form__row">
          <label className="field">
            <span className="field__label">Deposit / 1st payment (£) — optional</span>
            <input className="input" inputMode="decimal" value={firstAmount} onChange={(e) => setFirstAmount(e.target.value)} placeholder="If the first is bigger" />
          </label>
          <label className="field">
            <span className="field__label">Last payment (£) — optional</span>
            <input className="input" inputMode="decimal" value={lastAmount} onChange={(e) => setLastAmount(e.target.value)} placeholder="If the last differs" />
          </label>
        </div>
        <p className="field__hint">Leave both blank if every payment is the same. The middle payments use the amount above.</p>
        <div className="form__row">
          <label className="field">
            <span className="field__label">Due day of month</span>
            <input className="input" inputMode="numeric" value={dueDay} onChange={(e) => setDueDay(e.target.value)} placeholder="15" />
          </label>
          <div className="field">
            <span className="field__label">Paid by</span>
            <Select
              value={paidBy}
              onChange={setPaidBy}
              placeholder="—"
              ariaLabel="Paid by"
              options={[{ value: "", label: "—" }, ...data.household.members.map((m) => ({ value: String(m.id), label: m.name }))]}
            />
          </div>
        </div>
        <div className="field">
          <span className="field__label">Category</span>
          <Select
            value={categoryId}
            onChange={setCategoryId}
            placeholder="—"
            ariaLabel="Category"
            options={[{ value: "", label: "—" }, ...data.categories.map((c) => ({ value: String(c.id), label: c.name }))]}
          />
        </div>
        {error && <p className="form__error">{error}</p>}
        <div className="form__actions form__actions--split">
          {editing ? <button className="btn btn--danger" onClick={remove} disabled={saving}>Archive</button> : <span />}
          <div className="form__actions">
            <button className="btn btn--ghost" onClick={onClose}>Cancel</button>
            <button className="btn btn--primary" onClick={save} disabled={saving}>
              {saving ? "Saving…" : editing ? "Save plan" : "Add plan"}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
